import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { 
  Building2, 
  Shield, 
  Heart, 
  Stethoscope, 
  School, 
  Hotel,
  Home,
  Factory,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { Button } from './ui/button';

const IndustryCard = ({ icon: Icon, title, subtitle, description, color, delay = 0 }) => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      ref={ref}
      className={`group relative transition-all duration-1000 ${inView ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}
      style={{ transitionDelay: `${delay}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-full">
        {/* Dynamic Glow Effect */}
        <div className={`absolute -inset-1 bg-gradient-to-r ${color} rounded-2xl blur opacity-0 group-hover:opacity-30 transition-all duration-500`}></div>
        
        {/* Card Content */}
        <div className="relative bg-white rounded-2xl p-6 h-full shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-gray-100">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="w-8 h-8 text-white" />
          </div>

          {/* Title & Subtitle */}
          <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
            {title}
          </h3>
          <div className={`text-sm font-semibold mb-3 bg-gradient-to-r ${color} bg-clip-text text-transparent`}>
            {subtitle}
          </div>

          {/* Description */}
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            {description}
          </p>

          {/* CTA */}
          <div className="flex items-center text-blue-600 font-medium text-sm group-hover:text-cyan-500 transition-colors duration-300">
            <span>Learn More</span>
            <ArrowRight className={`ml-2 w-4 h-4 transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const NewIndustriesSection = () => {
  const industries = [
    {
      icon: Building2,
      title: "Pharmacies",
      subtitle: "Medications & Opioids",
      description: "Render drugs non-divertible and non-retrievable with our patented biodegradable solution.",
      color: "from-indigo-500 to-indigo-600"
    },
    {
      icon: Stethoscope,
      title: "Hospitals",
      subtitle: "On-Site Drug Disposal",
      description: "Hospitals, clinics, and healthcare facilities trust NarcX™ for safe, compliant medication disposal.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: Shield,
      title: "Law Enforcement",
      subtitle: "Illicit Drugs - Fentanyl",
      description: "Trusted by Various Law Enforcement and Federal Agencies for Illicit Drug Disposal.",
      color: "from-sky-500 to-sky-600"
    },
    {
      icon: Heart,
      title: "Addiction Recovery",
      subtitle: "Safe Disposal Solution",
      description: "Treatment facilities use NarcX™ to safely dispose of surrendered medications and substances.",
      color: "from-cyan-500 to-cyan-600"
    },
    {
      icon: School,
      title: "Educational Institutions",
      subtitle: "Campus Safety",
      description: "Universities and schools maintain drug-free environments with secure disposal solutions.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: Hotel,
      title: "Hospitality",
      subtitle: "Guest Safety",
      description: "Hotels and venues protect guests by safely disposing of found substances and medications.",
      color: "from-pink-500 to-pink-600"
    },
    {
      icon: Home,
      title: "Residential",
      subtitle: "At-Home Disposal",
      description: "Families and individuals safely dispose of unused medications in their homes.",
      color: "from-green-500 to-green-600"
    },
    {
      icon: Factory,
      title: "Corporate",
      subtitle: "Workplace Safety",
      description: "Companies maintain safe work environments with compliant disposal protocols.",
      color: "from-orange-500 to-orange-600"
    }
  ];

  const [headerRef, headerInView] = useInView({ threshold: 0.3, triggerOnce: true });

  return (
    <section className="py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-1000 ${headerInView ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-6">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Trusted Drug Disposal Across Multiple Industries
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6">
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Industries</span> We Serve
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            From hospitals to households, law enforcement to recovery centers, NarcX™ provides safe, compliant drug disposal solutions for every sector.
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {industries.map((industry, index) => (
            <IndustryCard
              key={index}
              icon={industry.icon}
              title={industry.title}
              subtitle={industry.subtitle}
              description={industry.description}
              color={industry.color}
              delay={index * 100}
            />
          ))}
        </div>

        {/* CTA Section with Enhanced 3D Pills */}
        <div className="relative overflow-hidden">
          {/* Enhanced 3D Pills Background */}
          <div className="absolute inset-0 pointer-events-none z-0">
            <div className="relative w-full h-full">
              {/* More pills with enhanced visibility */}
              {[...Array(15)].map((_, i) => {
                const colors = [
                  'from-red-500 to-red-700',
                  'from-blue-500 to-blue-700', 
                  'from-green-500 to-green-700',
                  'from-yellow-500 to-orange-600',
                  'from-purple-500 to-purple-700',
                  'from-pink-500 to-pink-700',
                  'from-indigo-500 to-indigo-700',
                  'from-teal-500 to-teal-700'
                ];
                const sizes = ['w-6 h-12', 'w-8 h-8', 'w-10 h-6', 'w-7 h-14', 'w-9 h-5', 'w-12 h-8'];
                const opacities = ['opacity-85', 'opacity-90', 'opacity-95', 'opacity-80'];
                
                return (
                  <div
                    key={i}
                    className={`pill-animation absolute ${sizes[i % sizes.length]} bg-gradient-to-br ${colors[i % colors.length]} rounded-full ${opacities[i % opacities.length]}`}
                    style={{
                      left: `${5 + Math.random() * 90}%`,
                      top: `${5 + Math.random() * 90}%`,
                      animation: `float ${5 + Math.random() * 5}s ease-in-out infinite ${Math.random() * 2}s, rotate ${10 + Math.random() * 10}s linear infinite`
                    }}
                  />
                );
              })}
            </div>
          </div>
          
          <div className="relative bg-white/85 rounded-3xl p-12 border border-gray-200/50 backdrop-blur-sm z-10 shadow-xl">
            <div className="text-center">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Ready to Implement NarcX™ in Your Organization?
              </h3>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                Join thousands of organizations already using NarcX™ for safe, compliant, and environmentally friendly drug disposal.
              </p>
              <a href="#contact">
                <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-10 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl group">
                  Let's Talk
                  <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};